import React, { useCallback, useState } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import { useStore } from 'store';
import { KsBoosterMarketingService, SetKSBoosterSettingsRequest } from 'api/client';

import { Typography } from 'components/dataDisplay';
import { Modal } from 'components/feedback';
import { Button, Checkbox } from 'components/inputs';

import successImage from 'static/images/success-emoji.png';

import styles from './KsBoosterSettingsModal.module.scss';

const KsBoosterSettingsModal: React.FC = () => {
  const {
    ksBoosterMarketingStore: { isSettingsModalOpen, closeSettingsModal, getSettings },
  } = useStore();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedBonusType, setSelectedBonusType] = useState<SetKSBoosterSettingsRequest['bonusType'] | null>(null);

  const handleClose = useCallback(() => {
    closeSettingsModal();
  }, [closeSettingsModal]);

  const handleSubmit = useCallback(async () => {
    if (!selectedBonusType) return;
    try {
      setIsLoading(true);
      await KsBoosterMarketingService.setKsBoosterSettings({
        bonusType: selectedBonusType,
      });
      await getSettings();
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBonusType, getSettings]);

  return (
    <Modal className={styles.KsBoosterSettingsModal} isOpen={isSettingsModalOpen} onClose={handleClose}>
      {!isSuccess && (
        <>
          <Typography className={styles.title} variant="heading6" element="h2">
            {t('modals.ksBoosterSettingsModal.title')}
          </Typography>

          <Typography className={styles.text} variant="paragraph1" element="p">
            <Trans
              i18nKey="modals.ksBoosterSettingsModal.text"
              components={{
                b: <b />,
                span: <span />,
                br: <br />,
              }}
            />
          </Typography>

          <Typography className={styles.label} variant="paragraph1" element="p">
            {t('modals.ksBoosterSettingsModal.label')}
          </Typography>

          <div className={styles.options}>
            <Checkbox
              className={styles.option}
              checked={selectedBonusType === SetKSBoosterSettingsRequest.bonusType.SPEED_BOX}
              onChange={() => setSelectedBonusType(SetKSBoosterSettingsRequest.bonusType.SPEED_BOX)}
              disabled={isLoading}
            >
              <Typography variant="paragraph1" element="span">
                SpeedBox
              </Typography>
            </Checkbox>

            <Checkbox
              className={styles.option}
              checked={selectedBonusType === SetKSBoosterSettingsRequest.bonusType.KLONDIKE}
              onChange={() => setSelectedBonusType(SetKSBoosterSettingsRequest.bonusType.KLONDIKE)}
              disabled={isLoading}
            >
              <Typography variant="paragraph1" element="span">
                Klondike
              </Typography>
            </Checkbox>
          </div>

          <Button className={styles.submit} onClick={handleSubmit} isLoading={isLoading} disabled={!selectedBonusType}>
            {t('modals.ksBoosterSettingsModal.submit')}
          </Button>

          <Button className={styles.button} color="red" onClick={handleClose} disabled={isLoading}>
            {t('shared.controls.cancel')}
          </Button>
        </>
      )}

      {isSuccess && (
        <>
          <Typography className={styles.title} variant="heading6" element="h2">
            {t('modals.ksBoosterSettingsModal.success.title', { bonusType: selectedBonusType })}
          </Typography>

          <img className={styles.successImage} src={successImage} alt="success" />

          <Button className={styles.button} onClick={handleClose}>
            {t('modals.ksBoosterSettingsModal.success.close')}
          </Button>
        </>
      )}
    </Modal>
  );
};

export default observer(KsBoosterSettingsModal);
